import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import { toast } from "sonner";

const empty = { code: "", discount_type: "percentage", value: 10, expires_at: "", usage_limit: 0, min_order: 0, active: true };

export default function AdminCoupons() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/admin/coupons", { ...form, value: +form.value, usage_limit: +form.usage_limit, min_order: +form.min_order, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null }); toast.success("Coupon created"); setOpen(false); setForm(empty); load(); }
    catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (c) => { if (!window.confirm("Delete coupon?")) return; await api.delete(`/admin/coupons/${c.id}`); load(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="Coupons" action={<button onClick={() => { setForm(empty); setOpen(true); }} className="gold-btn text-sm" data-testid="admin-add-coupon"><Plus size={16} /> Add Coupon</button>} />
      <div className="card-lux overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="admin-coupons-table">
          <thead className="bg-black/30 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Discount</th><th className="text-left px-5 py-3">Used</th><th className="text-left px-5 py-3">Min Order</th><th className="text-left px-5 py-3">Active</th><th className="text-right px-5 py-3"></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`coupon-row-${c.code}`}>
                <td className="px-5 py-3 font-mono text-amber-400">{c.code}</td>
                <td className="px-5 py-3 text-white">{c.discount_type === "percentage" ? `${c.value}%` : `${c.value} USDT`}</td>
                <td className="px-5 py-3 text-slate-400">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                <td className="px-5 py-3 text-slate-400">{c.min_order} USDT</td>
                <td className="px-5 py-3">{c.active ? <span className="text-emerald-400 text-xs">Yes</span> : <span className="text-slate-500 text-xs">No</span>}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => del(c)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No coupons yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Coupon">
        <form onSubmit={save} className="space-y-4" data-testid="coupon-form">
          <Field label="Coupon Code"><input required className="input-lux uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="coupon-code-field" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type"><select className="input-lux" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}><option value="percentage">Percentage</option><option value="fixed">Fixed (USDT)</option></select></Field>
            <Field label="Value"><input type="number" className="input-lux" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Usage Limit"><input type="number" className="input-lux" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} /></Field>
            <Field label="Min Order"><input type="number" className="input-lux" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} /></Field>
            <Field label="Expires"><input type="date" className="input-lux" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-amber-500" /> Active</label>
          <button disabled={saving} className="gold-btn w-full" data-testid="coupon-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Create Coupon"}</button>
        </form>
      </Modal>
    </div>
  );
}
